import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, ListChecks, Settings2, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { ProgressStepsDialog } from "@/components/ProgressStepsDialog";
import { fmtMoney } from "@/pages/biz/Offers";
import { api, useFetch, errMsg, fmtDate } from "@/lib/api";
import { useAuth, useCurrency } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

function Step({ o, s, onChanged }) {
  const [note, setNote] = useState(s.note || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/biz/offers/${o.id}/progress/${s.id}`, body); if (msg) toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const late = !s.done && s.target_date && s.target_date < new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-2 py-2" data-testid={`step-row-${s.id}`}>
      <button onClick={() => patch({ done: !s.done }, s.done ? "Step reopened" : `${s.name} done`)} className="flex items-center gap-2 text-left md:w-64 shrink-0" data-testid={`step-toggle-${s.id}`}>
        {s.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
        <span className={cn("text-sm font-medium", s.done && "text-slate-500")}>{s.name}</span>
      </button>
      <div className="text-xs text-slate-500 md:w-56">{s.done ? `Done ${fmtDate(s.done_at)}` : <span className="flex items-center gap-1">Target
        <Input type="date" className={cn("h-7 w-36 text-xs", late && "border-rose-400 text-rose-600")} value={s.target_date || ""} onChange={(e) => patch(e.target.value ? { target_date: e.target.value } : { clear_target: true })} data-testid={`step-target-${s.id}`} /></span>}</div>
      <Input className="h-8 text-sm flex-1" placeholder="Note (e.g. references back Tuesday)" value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (s.note || "") && patch({ note }, "Note saved")} data-testid={`step-note-${s.id}`} />
    </div>
  );
}

function Deal({ o, cur, onChanged }) {
  const steps = o.progress || [];
  const done = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
  const next = steps.find((s) => !s.done);
  return (
    <div className="panel" data-testid={`deal-${o.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <div><div className="font-bold">{o.listing_title}</div>
          <div className="text-sm text-slate-500">{o.applicant_name} · {fmtMoney(o.amount, cur, o.channel)} · accepted {fmtDate(o.decided_at)}</div></div>
        <div className="text-right">{o.progress_completed_at ? <span className="text-emerald-700 font-semibold flex items-center gap-1" data-testid={`deal-complete-${o.id}`}><Trophy className="w-4 h-4" />{o.channel === "sale" ? "Completed" : "Moved in"}</span>
          : <span className="text-sm" data-testid={`deal-next-${o.id}`}>Next: <b>{next?.name}</b></span>}</div>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1"><div className="h-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid={`deal-bar-${o.id}`} /></div>
      <div className="text-xs text-slate-500 mb-2">{done} of {steps.length} steps · {pct}%</div>
      <div className="divide-y divide-slate-100">{steps.map((s) => <Step key={s.id} o={o} s={s} onChanged={onChanged} />)}</div>
    </div>
  );
}

export default function Progress() {
  const cur = useCurrency();
  const { session } = useAuth();
  const { data, reload } = useFetch("/biz/progress");
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useState("open");
  const list = (data || []).filter((o) => (tab === "open" ? !o.progress_completed_at : o.progress_completed_at));
  return (
    <div data-testid="progress-page">
      <PageHeader eyebrow="Sales & lettings" title="Progress" subtitle="Move each accepted offer from agreed to move-in or completion. Visible to your team only."
        actions={<Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-steps-button"><Settings2 className="w-4 h-4 mr-1" />Progress steps</Button>} />
      <div className="flex gap-2 mb-5">{[["open", "In progress"], ["done", "Completed"]].map(([k, l]) => (
        <button key={k} onClick={() => setTab(k)} data-testid={`progress-tab-${k}`} className={cn("px-3 py-1.5 rounded-full border text-sm transition-colors", tab === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-slate-900")}>{l}</button>))}</div>
      {!data ? <Loading /> : list.length === 0 ? <EmptyState icon={ListChecks} title={tab === "open" ? "Nothing in progress" : "Nothing completed yet"} text="Accepted offers appear here with their checklist." /> : (
        <div className="space-y-4">{list.map((o) => <Deal key={o.id} o={o} cur={cur} onChanged={reload} />)}</div>)}
      <ProgressStepsDialog open={edit} onOpenChange={setEdit} disabled={session.user.id !== session.business.owner_user_id} />
    </div>
  );
}
